/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React from 'react'
import PropTypes from 'prop-types'
import {FormattedMessage, FormattedNumber, useIntl} from 'react-intl'

// Chakra Components
import {
    Box,
    Flex,
    Stack,
    Text,
    VisuallyHidden,
    useStyleConfig,
    HStack,
    Skeleton,
    Image,
    Button,
    Divider,
    AccordionItem,
    Accordion,
    AccordionPanel,
    AccordionButton,
} from '@chakra-ui/react'
import Link from '@salesforce/retail-react-app/app/components/link'

// Project Components
import CartItemVariantImage from '../item-variant/item-image'
import CartItemVariantName from '../../components/item-variant/item-name'
import QuantityPicker from '../../components/quantity-picker'
import {noop} from '@salesforce/retail-react-app/app/utils/utils'
import {useCurrency} from '@salesforce/retail-react-app/app/hooks'
import {useDerivedProduct} from '../../hooks/use-derived-product'
import UnitPrice from '../../pages/cart/partials/unit-price'
import StockLabel from '../stock-label'
import {InfoIcon, CloseIcon, InfoLabelIcon, CloseLabelIcon, ChevronUp, ChevronDown} from '../icons'
import OmnibusPrice from '../display-price/omnibus-price'
import {InformativeLabelGroup} from '../informative-label'
import VatLabel from '../vat-label'
import useProductData, {addParametersToPath} from '../../hooks/use-product-data'
import ItemVariantProvider from '@salesforce/retail-react-app/app/components/item-variant'
import {remCalc} from '../../theme/_utils'
import {getOrderAvailabilityStatus, getFutureStockDate} from '../../utils/order-availability-utils'
import {
    useCustomPreference,
    useBooleanCustomPreference,
    useCustomPreferenceWithLocale,
} from '../../hooks/use-custom-preference'
import {useGoogleTagManager} from '../../custom-api/hooks/use-google-tag-manager'
import {item_gtm_event, polish_ecommerce_gtm_event} from '../../custom-api/layers/default'
import {getPriceData} from '../../utils/product-utils'
import LoadingSpinner from '../loading-spinner'
import {BRAND_KEY} from '../../brand'
import useCorporateSale from '../../hooks/use-corporate-sale'
import {useMaxQuantityItems} from '../../hooks/use-max-quantity-items'
import WarrantySectionCart from './warranty-section-cart'

/**
 * Component representing a product item usually in a list with details about the product - name, variant, pricing, etc.
 * @param {Object} product Product to be represented in the list item.
 * @param {node} primaryAction Child component representing the most prominent action to be performed by the user.
 * @param {node} secondaryActions Child component representing the other actions relevant to the product to be performed by the user.
 * @param {func} onItemQuantityChange callback function to be invoked whenever item quantity changes.
 * @param {boolean} showLoading Renders a loading spinner with overlay if set to true.
 * @returns A JSX element representing product item in a list (eg: wishlist, cart, etc).
 */
const ProductItem = ({
    product,
    primaryAction,
    secondaryActions,
    onItemQuantityChange = noop,
    showLoading = false,
    officinaEditParams,
    officinaImage,
    index = undefined,
    isCart = false,
    onAddToCartModalOpen = noop,
}) => {
    const {quantity, stepQuantity, setQuantity} = useDerivedProduct(product)
    const {currency: activeCurrency} = useCurrency()
    const {formatMessage} = useIntl()
    const style = useStyleConfig('ProductItem')
    const {addLayer} = useGoogleTagManager()
    const InfoStockIcon = BRAND_KEY === 'braun' ? InfoLabelIcon : InfoIcon
    const OutOfStockIcon = BRAND_KEY === 'braun' ? CloseLabelIcon : CloseIcon
    const OutOfStockVariant = BRAND_KEY === 'braun' ? 'braun-out-of-stock' : 'outOfStock'
    const LastFewItemsVariant = BRAND_KEY === 'braun' ? 'braun-last-few-items' : 'lastFewItems'
    const {isCorporateSale, corporateSaleLabelName} = useCorporateSale(product)

    const {productUrl, priceData} = useProductData(product)
    const orderStatus = getOrderAvailabilityStatus(product)
    const futureStockDate = getFutureStockDate(product, formatMessage.locale || 'en-GB')
    const {getMaxQuantity} = useMaxQuantityItems()
    const warrantiesRecommendations = product?.c_recommendations?.filter(
        (rec) => rec?.recommendationType?.value === 5 && rec.warrantyGroupId,
    )

    const warrantyTermsAndConditionsLink = useCustomPreferenceWithLocale(
        'warrantyTermsAndConditionsLink',
    )

    const warranties = warrantiesRecommendations
        ?.map((rec) => {
            const validVariants = (rec.variants || [])
                .filter((variant) => variant && variant.productId)
                .map((variant) => ({
                    id: variant.productId,
                    name: variant.productName,
                    price: variant.price,
                    serviceDuration: variant.serviceDuration,
                }))

            if (validVariants.length === 0) {
                return null
            }

            return {
                groupName: rec.warrantyGroupName,
                groupId: rec.warrantyGroupId,
                options: validVariants,
            }
        })
        .filter(Boolean)
    const hasWarranties = warranties && warranties.length > 0

    const officinaPageEditLink = addParametersToPath(productUrl, [
        {name: 'tkcsid', value: officinaEditParams},
    ])

    const showDiscount =
        !!priceData?.tieredPrice &&
        priceData?.currentPrice / product?.quantity < priceData?.listPrice
    const hasBonusProduct = !!product?.bonusProductLineItem

    // For preorder/backorder products, use custom preference limit; otherwise use stock level
    const isPreorderOrBackorder = orderStatus.isPreorder || orderStatus.isBackorder
    const stockLevel = isPreorderOrBackorder
        ? getMaxQuantity(product) || 2 // Use custom preference limit or default to 2
        : product?.inventory?.stockLevel // Use actual stock level for normal products

    const lastItemThreshold = useCustomPreference('showLastItemThreshold')
    const isVATFlag = useBooleanCustomPreference('showVATFlag')

    const isFreeGift =
        product?.priceAfterItemDiscount === 0 || product?.priceAfterOrderDiscount === 0

    const triggerSelectItemGtm = async () => {
        await addLayer(polish_ecommerce_gtm_event.bind(this))
        await addLayer(
            item_gtm_event.bind(this, {
                event: 'select_item',
                item_list_name: product?.c_defaultCategoryName,
                items: [
                    {
                        item_name: product?.c_defaultName,
                        item_id: product?.productId || product?.id,
                        item_category: product?.c_defaultCategoryName,
                        item_variant: product?.productId || product?.id,
                        price: (product?.price - product?.tax).toFixed(2),
                        fullprice: getPriceData(product)?.isOnSale ? 'reduced' : 'fullprice',
                        rating_points: '',
                        // get_reevoo_rating() || get_bv_rating(),
                        erp_category: product?.c_materialGroup,
                        prdha_category: product?.c_prdhaCategory || product?.c_prdha_category,
                        index: index + 1,
                        stock_status: 'in-stock', //?What to do here
                    },
                ],
            }),
        )
    }

    return (
        <Box
            {...style?.itemContainer}
            data-testid={`sf-cart-item-${product.productId ? product.productId : product.id}`}
        >
            <ItemVariantProvider variant={product}>
                {showLoading && <LoadingSpinner />}
                <Flex {...style?.flexContainer}>
                    {officinaImage && officinaEditParams ? (
                        <a
                            href={officinaPageEditLink}
                            aria-label={formatMessage({
                                id: 'product_item.link.officina.label',
                                defaultMessage: 'View personalized product ',
                            })}
                        >
                            <Box position="relative" {...style?.cartImage}>
                                <Image
                                    alt={officinaEditParams}
                                    src={officinaImage}
                                    ignoreFallback={true}
                                    {...style?.cartImage}
                                    role="img"
                                />
                            </Box>
                        </a>
                    ) : (
                        <Link
                            to={productUrl}
                            onClick={triggerSelectItemGtm}
                            aria-label={formatMessage({
                                id: 'product_item.link.product.label',
                                defaultMessage: 'View product details for product',
                            })}
                        >
                            <CartItemVariantImage
                                {...style?.cartImage}
                                imageProps={style?.cartImage}
                            />
                        </Link>
                    )}

                    <Box {...style?.detailContainer}>
                        <Flex {...style?.infoLabelContainer}>
                            <InformativeLabelGroup
                                product={product?.representedProduct ?? product}
                                priceData={priceData}
                                showPromotions={true}
                                isFreeGift={isFreeGift}
                                isCorporateSale={isCorporateSale}
                                corporateSaleLabelName={corporateSaleLabelName}
                            />
                        </Flex>
                        <Box {...style?.headerStack} gap={remCalc(8)}>
                            <Link
                                to={productUrl}
                                onClick={triggerSelectItemGtm}
                                className="dln-cartItem__title"
                                aria-label={formatMessage({
                                    id: 'product_item.link.product.label',
                                    defaultMessage: 'product name',
                                })}
                            >
                                <CartItemVariantName {...style?.itemName} />
                            </Link>
                            <Box {...style?.removeIcon}>{secondaryActions}</Box>
                        </Box>
                        <Box {...style?.omniAndPrice}>
                            {isFreeGift ? (
                                <>
                                    <FormattedNumber
                                        style="currency"
                                        currency={activeCurrency}
                                        value={0}
                                    />
                                    <Box {...style?.omnibus}>
                                        <Box {...style?.suggestedPrice}>
                                            <UnitPrice
                                                currency={activeCurrency}
                                                hasStrikethrough={true}
                                            />
                                        </Box>
                                        <Text {...style?.suggestedText}>
                                            {formatMessage({
                                                id: 'item_variant.suggested_price',
                                                defaultMessage: 'Suggested Price',
                                            })}
                                        </Text>
                                    </Box>
                                </>
                            ) : (
                                <>
                                    <UnitPrice
                                        currency={activeCurrency}
                                        style={style?.itemPrice}
                                        isCart={true}
                                    />
                                    {showDiscount && !hasBonusProduct && (
                                        <Box {...style?.omnibus}>
                                            {priceData.showOmnibusPriceDiscount ? (
                                                <OmnibusPrice
                                                    priceData={priceData}
                                                    currency={activeCurrency}
                                                    omnibusExpandedView={true}
                                                    variant={'recommended'}
                                                    showDiscount={false}
                                                />
                                            ) : priceData.listPrice ? (
                                                <>
                                                    <Box {...style?.suggestedPrice}>
                                                        <UnitPrice
                                                            currency={activeCurrency}
                                                            hasStrikethrough={true}
                                                            isCart={isCart}
                                                        />
                                                    </Box>
                                                    <Text {...style?.suggestedText}>
                                                        {formatMessage({
                                                            id: 'item_variant.suggested_price',
                                                            defaultMessage: 'Suggested Price',
                                                        })}
                                                    </Text>
                                                </>
                                            ) : (
                                                <Skeleton h={remCalc(32)} w="100%" />
                                            )}
                                        </Box>
                                    )}
                                </>
                            )}
                        </Box>
                        {isVATFlag && !isFreeGift && (
                            <Box {...style?.vatDetails}>
                                <HStack>
                                    <VatLabel
                                        price={priceData?.pricePerUnit || priceData.currentPrice}
                                        vatRate={product?.taxRate * 100}
                                        variant="vatNoWrap"
                                    />
                                </HStack>
                            </Box>
                        )}

                        {product?.c_isOfficina === 'true' && (
                            <Box {...style?.officinaLink}>
                                <HStack fontWeight={'bold'}>
                                    <a href={officinaPageEditLink}>
                                        {formatMessage({
                                            id: 'item_variant.edit_personalization',
                                            defaultMessage: 'Edit personalization',
                                        })}
                                    </a>
                                </HStack>
                            </Box>
                        )}
                        {stockLevel > 0 &&
                            stockLevel <= lastItemThreshold &&
                            !orderStatus.isPreorder &&
                            !orderStatus.isBackorder && (
                                <StockLabel
                                    variant={LastFewItemsVariant}
                                    icon={<InfoStockIcon circleColor="transparent" />}
                                    textMessage={
                                        <FormattedMessage
                                            id="use_product.message.low_stock"
                                            defaultMessage="Last {quantity} {itemLabel}"
                                            values={{
                                                quantity: stockLevel,
                                                itemLabel:
                                                    stockLevel === 1 ? (
                                                        <FormattedMessage
                                                            id="global.message.item"
                                                            defaultMessage="item"
                                                        />
                                                    ) : (
                                                        <FormattedMessage
                                                            id="global.message.items"
                                                            defaultMessage="items"
                                                        />
                                                    ),
                                            }}
                                        />
                                    }
                                />
                            )}
                        {stockLevel === 0 && !orderStatus.isPreorder && !orderStatus.isBackorder ? (
                            <StockLabel
                                variant={OutOfStockVariant}
                                icon={<OutOfStockIcon />}
                                textMessage={
                                    <FormattedMessage
                                        id="use_product.message.out_of_stock"
                                        defaultMessage="Out of stock"
                                    />
                                }
                            />
                        ) : (
                            <>
                                {!hasBonusProduct && (
                                    <Box
                                        {...style?.quantSelContainer}
                                        className="dln-orderSumm__contentRow"
                                    >
                                        <Text
                                            {...style?.textQuantity}
                                            aria-label={formatMessage(
                                                {
                                                    id: 'item_variant.quantity.label',
                                                    defaultMessage:
                                                        'Quantity selector for {productName}. Selected quantity is {quantity}',
                                                },
                                                {
                                                    quantity: product?.quantity,
                                                    productName:
                                                        product?.c_variantIdentifier ||
                                                        product?.name ||
                                                        product?.productName,
                                                },
                                            )}
                                        >
                                            <FormattedMessage
                                                defaultMessage="Quantity:"
                                                id="product_item.label.quantity"
                                            />
                                        </Text>
                                        <QuantityPicker
                                            step={stepQuantity}
                                            value={quantity}
                                            min={0}
                                            max={stockLevel}
                                            clampValueOnBlur={false}
                                            onBlur={(e) => {
                                                const {value} = e.target
                                                if (!value) {
                                                    setQuantity(product.quantity)
                                                }
                                            }}
                                            onChange={(stringValue, numberValue) => {
                                                if (numberValue >= 0) {
                                                    onItemQuantityChange(numberValue).then(
                                                        (isValidChange) =>
                                                            isValidChange &&
                                                            setQuantity(numberValue),
                                                    )
                                                } else if (stringValue === '') {
                                                    setQuantity(stringValue)
                                                }
                                            }}
                                            productName={
                                                product?.c_variantIdentifier ||
                                                product?.name ||
                                                product?.productName
                                            }
                                        />
                                        <VisuallyHidden role="status">
                                            {product?.c_variantIdentifier ||
                                                product?.name ||
                                                product?.productName}
                                            {formatMessage(
                                                {
                                                    id: 'item_variant.assistive_msg.quantity',
                                                    defaultMessage: 'Quantity {quantity}',
                                                },
                                                {
                                                    quantity: product?.quantity,
                                                },
                                            )}
                                        </VisuallyHidden>
                                    </Box>
                                )}
                                {hasWarranties && (
                                    <Flex {...style.warrantyDesktopContainer}>
                                        <Divider />
                                        <Flex {...style.warrantyTitle} paddingTop={remCalc(16)}>
                                            <FormattedMessage
                                                id="warranty_section.label.extend_warranty"
                                                defaultMessage="Extend your warranty"
                                            />
                                        </Flex>
                                        <WarrantySectionCart
                                            warranties={warranties}
                                            product={product}
                                            warrantyTermsAndConditionsLink={
                                                warrantyTermsAndConditionsLink
                                            }
                                        />
                                    </Flex>
                                )}
                            </>
                        )}
                        {orderStatus.isPreorder && futureStockDate && (
                            <Flex {...style?.preorderCartContainer}>
                                <Text {...style?.preorderCartTitleText}>
                                    <FormattedMessage
                                        id="product_item.label.preorder"
                                        defaultMessage="Preorder:"
                                    />
                                </Text>
                                <Text {...style?.preorderCartDescriptionText}>
                                    <FormattedMessage
                                        id="order_summary.message.available_from"
                                        defaultMessage="This item will be available from {date}"
                                        values={{
                                            date: (
                                                <Text as="span" {...style?.preorderCartDateText}>
                                                    {futureStockDate}
                                                </Text>
                                            ),
                                        }}
                                    />
                                </Text>
                            </Flex>
                        )}
                        {orderStatus.isBackorder && futureStockDate && (
                            <Flex {...style?.backorderCartContainer}>
                                <Text {...style?.backorderCartTitleText}>
                                    <FormattedMessage
                                        id="product_item.label.backorder"
                                        defaultMessage="Backorder:"
                                    />
                                </Text>
                                <Text {...style?.backorderCartDescriptionText}>
                                    <FormattedMessage
                                        id="order_summary.message.available_from"
                                        defaultMessage="This item will be available from {date}"
                                        values={{
                                            date: (
                                                <Text as="span" {...style?.backorderCartDateText}>
                                                    {futureStockDate}
                                                </Text>
                                            ),
                                        }}
                                    />
                                </Text>
                            </Flex>
                        )}
                        {primaryAction && (
                            <Stack>
                                <Box display={['none', 'block', 'block', 'block']}>
                                    {primaryAction}
                                </Box>
                            </Stack>
                        )}
                        {/* {product && showInventoryMessage && (
                            <Box>
                                <Fade in={true}>
                                    <Text color="orange.600" fontWeight={600}>
                                        {inventoryMessage}
                                    </Text>
                                </Fade>
                            </Box>
                        )} */}
                        {product?.c_recommendations?.find(
                            (rec) => rec?.recommendationType?.value === 4,
                        ) && (
                            <>
                                <Text {...style?.subAndSaveText}>
                                    <FormattedMessage
                                        defaultMessage="Subscribe and save"
                                        id="product_item.subscription.label.subscribe_and_save"
                                    />
                                </Text>
                                <Button
                                    variant="secondary"
                                    onClick={() =>
                                        onAddToCartModalOpen({
                                            itemsAdded: [{product, quantity: 1}],
                                            isSubscription: true,
                                        })
                                    }
                                    {...style?.subAndSaveButton}
                                >
                                    <FormattedMessage
                                        defaultMessage="Buy in subscription"
                                        id="product_item.subscription.button.buy_subscription"
                                    />
                                </Button>
                            </>
                        )}
                    </Box>
                </Flex>
                {hasWarranties && (
                    <Flex display={{base: 'block', md: 'none'}} width="100%">
                        <Accordion allowToggle>
                            <AccordionItem {...style.warrantyAccordionItem}>
                                {({isExpanded}) => (
                                    <>
                                        <AccordionButton {...style.warrantyButtonAccordion}>
                                            <FormattedMessage
                                                id="warranty_section.label.extend_warranty"
                                                defaultMessage="Extend your warranty"
                                            />
                                            {isExpanded ? <ChevronUp /> : <ChevronDown />}
                                        </AccordionButton>
                                        <AccordionPanel {...style.warrantyAccordionPanel}>
                                            <WarrantySectionCart
                                                warranties={warranties}
                                                product={product}
                                                warrantyTermsAndConditionsLink={
                                                    warrantyTermsAndConditionsLink
                                                }
                                            />
                                        </AccordionPanel>
                                    </>
                                )}
                            </AccordionItem>
                        </Accordion>
                    </Flex>
                )}
                {primaryAction && (
                    <Box display={['block', 'none', 'none', 'none']} w={'full'}>
                        {primaryAction}
                    </Box>
                )}
            </ItemVariantProvider>
        </Box>
    )
}

ProductItem.propTypes = {
    product: PropTypes.object,
    onItemQuantityChange: PropTypes.func,
    onAddItemToCart: PropTypes.func,
    showLoading: PropTypes.bool,
    isWishlistItem: PropTypes.bool,
    primaryAction: PropTypes.node,
    secondaryActions: PropTypes.node,
    officinaEditParams: PropTypes.string,
    officinaImage: PropTypes.string,
    index: PropTypes.number,
    isCart: PropTypes.bool,
    onAddToCartModalOpen: PropTypes.func,
}

export default ProductItem
